import React, { useState, useEffect, useRef, useContext } from "react"; // Add useContext
import { motion } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, Text } from "@mantine/core";
import "./edit-profile.css";
import Loading from "../loading/Loading";
import { GlobalContext } from "../../GlobalContext"; // Import GlobalContext

const EditProfile = ({ role = "patient" }) => {
  const navigate = useNavigate();
  const { notify } = useContext(GlobalContext); // Access notify from GlobalContext
  const [isProvider, setIsProvider] = useState(false);

  const [providerData, setProviderData] = useState({
    firstName: "",
    lastName: "",
    organizationName: "",
    specialty: "",
    phone: "",
    email: "",
  });

  const [patientData, setPatientData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    gender: "",
    email: "",
    dateOfBirth: { month: "", date: "", year: "" },
    address: "",
  });

  const [providerErrors, setProviderErrors] = useState({});
  const [patientErrors, setPatientErrors] = useState({});
  const [providerTouched, setProviderTouched] = useState({
    firstName: false,
    lastName: false,
    organizationName: false,
    specialty: false,
    phone: false,
    email: false,
  });
  const [patientTouched, setPatientTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    gender: false,
    email: false,
    month: false,
    date: false,
    year: false,
    address: false,
  });

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);

  const API_ENDPOINT = "/wp-json/dbsb/v1/user/update";
  const USER_DATA_ENDPOINT = "/wp-json/dbsb/v1/user/me";

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(USER_DATA_ENDPOINT, {
          method: "GET",
          credentials: "same-origin",
          headers: {
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();

        if (data.profile_picture) {
          setAvatar(data.profile_picture);
        }

        const userIsProvider = data.roles.includes("author");
        setIsProvider(userIsProvider);

        if (userIsProvider) {
          setProviderData({
            firstName: data.first_name || "",
            lastName: data.last_name || "",
            organizationName: data.organization_name || "",
            specialty: data.specialty || "",
            phone: data.phone || "",
            email: data.email || "",
          });
        } else {
          // Support DD/MM/YYYY and YYYY-MM-DD from API
          let dob = { month: "", date: "", year: "" };
          if (data.date_of_birth && typeof data.date_of_birth === "string") {
            const dobStr = data.date_of_birth.trim();
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) {
              const [day, month, year] = dobStr.split("/");
              dob = {
                month: month ? parseInt(month, 10) : "",
                date: day || "",
                year: year || "",
              };
            } else if (/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
              const [year, month, day] = dobStr.split("-");
              dob = {
                month: month ? parseInt(month, 10) : "",
                date: day || "",
                year: year || "",
              };
            }
          }
          setPatientData({
            firstName: data.first_name || "",
            lastName: data.last_name || "",
            phone: data.phone || "",
            gender: data.gender || "",
            email: data.email || "",
            dateOfBirth: dob,
            address: data.address || "",
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setFetchError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleProviderChange = (e) => {
    const { name, value } = e.target;
    setProviderData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProviderBlur = (e) => {
    const { name } = e.target;
    setProviderTouched((prev) => ({ ...prev, [name]: true }));
    validateProvider();
  };

  const handleProviderPhoneChange = (value) => {
    setProviderData((prev) => ({ ...prev, phone: value || "" }));
  };

  const handleProviderPhoneBlur = () => {
    setProviderTouched((prev) => ({ ...prev, phone: true }));
    validateProvider();
  };

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    setPatientData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePatientBlur = (e) => {
    const { name } = e.target;
    setPatientTouched((prev) => ({ ...prev, [name]: true }));
    validatePatient();
  };

  const handlePatientPhoneChange = (value) => {
    setPatientData((prev) => ({ ...prev, phone: value || "" }));
  };

  const handlePatientPhoneBlur = () => {
    setPatientTouched((prev) => ({ ...prev, phone: true }));
    validatePatient();
  };

  const handlePatientDateChange = (field, value) => {
    let sanitizedValue = value;

    if (field === "date") {
      sanitizedValue = value.slice(0, 2);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        setPatientData((prev) => ({
          ...prev,
          dateOfBirth: { ...prev.dateOfBirth, [field]: sanitizedValue },
        }));
      }
    } else if (field === "year") {
      sanitizedValue = value.slice(0, 4);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        setPatientData((prev) => ({
          ...prev,
          dateOfBirth: { ...prev.dateOfBirth, [field]: sanitizedValue },
        }));
      }
    } else if (field === "month") {
      const monthNum = value ? parseInt(value, 10) : "";
      if (monthNum >= 1 && monthNum <= 12) {
        setPatientData((prev) => ({
          ...prev,
          dateOfBirth: { ...prev.dateOfBirth, [field]: monthNum },
        }));
      }
    }
  };

  const handlePatientDateBlur = (field) => {
    setPatientTouched((prev) => ({ ...prev, [field]: true }));
    validatePatient();
  };

  const validateProvider = () => {
    const newErrors = {};
    const touched = providerTouched;

    if (touched.firstName && !providerData.firstName)
      newErrors.firstName = "First Name is required";
    if (touched.lastName && !providerData.lastName)
      newErrors.lastName = "Last Name is required";
    if (touched.organizationName && !providerData.organizationName)
      newErrors.organizationName = "Organization Name is required";
    if (touched.specialty && !providerData.specialty)
      newErrors.specialty = "Specialty is required";

    if (touched.phone) {
      if (!providerData.phone) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = providerData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (touched.email) {
      if (!providerData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(providerData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    setProviderErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePatient = () => {
    const newErrors = {};
    const touched = patientTouched;

    if (touched.firstName && !patientData.firstName)
      newErrors.firstName = "First Name is required";
    if (touched.lastName && !patientData.lastName)
      newErrors.lastName = "Last Name is required";
    if (touched.gender && !patientData.gender)
      newErrors.gender = "Gender is required";

    if (touched.phone) {
      if (!patientData.phone) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = patientData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (touched.email) {
      if (!patientData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(patientData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    const { month, date, year } = patientData.dateOfBirth;
    const currentYear = new Date().getFullYear();

    if (touched.month && !month) {
      newErrors.month = "Month is required";
    }

    if (touched.date) {
      if (!date) {
        newErrors.date = "Day is required";
      } else {
        const dateNum = Number(date);
        if (isNaN(dateNum) || dateNum < 1 || dateNum > 31) {
          newErrors.date = "Day is invalid";
        }
      }
    }

    if (touched.year) {
      if (!year) {
        newErrors.year = "Year is required";
      } else if (year.length === 4) {
        const yearNum = Number(year);
        if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
          newErrors.year = "Year is invalid";
        }
      }
    }

    if (touched.address && !patientData.address)
      newErrors.address = "Address is required";

    setPatientErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isProviderIncomplete = () => {
    return Object.values(providerData).some((value) => value === "");
  };

  const isPatientIncomplete = () => {
    const { dateOfBirth, ...otherFields } = patientData;
    const { month, date, year } = dateOfBirth;
    const isMonthDefault = !month || month === "";
    return (
      Object.values(otherFields).some((value) => value === "") ||
      isMonthDefault ||
      !date ||
      !year
    );
  };

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    setProviderTouched({
      firstName: true,
      lastName: true,
      organizationName: true,
      specialty: true,
      phone: true,
      email: true,
    });

    if (!validateProvider()) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const formData = new FormData();
      formData.append("first_name", providerData.firstName);
      formData.append("last_name", providerData.lastName);
      formData.append("organization_name", providerData.organizationName);
      formData.append("specialty", providerData.specialty);
      formData.append("phone", providerData.phone);
      formData.append("email", providerData.email);

      if (avatarFile) {
        formData.append("profile_picture", avatarFile);
      } else if (!avatar) {
        formData.append("profile_picture", ""); // Send empty string to remove avatar
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to update profile. Please try again."
        );
      }

      if (data.profile_picture) {
        setAvatar(data.profile_picture);
      } else {
        setAvatar(null);
      }
      setAvatarFile(null);
      setModalOpened(true);
      notify("USER_DATA_UPDATED");
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setPatientTouched({
      firstName: true,
      lastName: true,
      phone: true,
      gender: true,
      email: true,
      month: true,
      date: true,
      year: true,
      address: true,
    });

    if (!validatePatient()) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const dobFormatted = `${String(patientData.dateOfBirth.date).padStart(
        2,
        "0"
      )}/${String(patientData.dateOfBirth.month).padStart(2, "0")}/${
        patientData.dateOfBirth.year
      }`;

      const formData = new FormData();
      formData.append("first_name", patientData.firstName);
      formData.append("last_name", patientData.lastName);
      formData.append("phone", patientData.phone);
      formData.append("gender", patientData.gender);
      formData.append("email", patientData.email);
      formData.append("date_of_birth", dobFormatted);
      formData.append("address", patientData.address);

      if (avatarFile) {
        formData.append("profile_picture", avatarFile);
      } else if (!avatar) {
        formData.append("profile_picture", ""); // Send empty string to remove avatar
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to update profile. Please try again."
        );
      }

      if (data.profile_picture) {
        setAvatar(data.profile_picture);
      } else {
        setAvatar(null);
      }
      setAvatarFile(null);
      setModalOpened(true);
      notify("USER_DATA_UPDATED");
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeAvatar = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const imageUrl = URL.createObjectURL(file);
      setAvatar(imageUrl);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatar(null); // Update UI to show default avatar
    setAvatarFile(null); // Clear any pending file
    fileInputRef.current.value = ""; // Reset file input
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  if (isLoading) {
    return <Loading />;
  }

  if (fetchError) {
    return <div className="error-message">Error: {fetchError}</div>;
  }

  return (
    <div className="edit-profile-container">
      <motion.div
        className="edit-profile-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="edit-profile-card" variants={itemVariants}>
          <motion.div variants={itemVariants} className="profile-header">
            <h2>
              {isProvider
                ? `${providerData.firstName} ${providerData.lastName}`
                : `${patientData.firstName} ${patientData.lastName}`}
            </h2>
            <span>{isProvider ? "Health Provider" : "Patient"}</span>
          </motion.div>
          <div className="form-avatar-wrapper">
            <div className="form-content">
              {isProvider ? (
                <motion.form
                  onSubmit={handleProviderSubmit}
                  className="edit-profile-form"
                  variants={formVariants}
                >
                  <motion.div
                    variants={itemVariants}
                    className="form-group name-row"
                  >
                    <div className="name-input">
                      <label>First Name</label>
                      <input
                        name="firstName"
                        value={providerData.firstName}
                        onChange={handleProviderChange}
                        onBlur={handleProviderBlur}
                        placeholder="Enter first name"
                      />
                      {providerErrors.firstName && (
                        <span className="error">
                          {providerErrors.firstName}
                        </span>
                      )}
                    </div>
                    <div className="name-input">
                      <label>Last Name</label>
                      <input
                        name="lastName"
                        value={providerData.lastName}
                        onChange={handleProviderChange}
                        onBlur={handleProviderBlur}
                        placeholder="Enter last name"
                      />
                      {providerErrors.lastName && (
                        <span className="error">{providerErrors.lastName}</span>
                      )}
                    </div>
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Organization Name</label>
                    <input
                      name="organizationName"
                      value={providerData.organizationName}
                      onChange={handleProviderChange}
                      onBlur={handleProviderBlur}
                      placeholder="Enter your organization name"
                    />
                    {providerErrors.organizationName && (
                      <span className="error">
                        {providerErrors.organizationName}
                      </span>
                    )}
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Specialty</label>
                    <select
                      name="specialty"
                      value={providerData.specialty}
                      onChange={handleProviderChange}
                      onBlur={handleProviderBlur}
                    >
                      <option value="">Select from dropdown</option>
                      <option value="Radiation Oncologist">Radiation Oncologist</option>
                      <option value="Haematologist">Haematologist</option>
                      <option value="Anatomic Pathologist">Anatomic Pathologist</option>
                      <option value="Maxillofacial Surgeon">Maxillofacial Surgeon</option>
                      <option value="ENT Surgeon">ENT Surgeon</option>
                      <option value="Ophthalmologist">Ophthalmologist</option>
                      <option value="Orthopaedic Surgeon">Orthopaedic Surgeon</option>
                      <option value="Urologist">Urologist</option>
                      <option value="Gastroenterologist">Gastroenterologist</option>
                      <option value="Hepatobiliary Surgeon">Hepatobiliary Surgeon</option>
                      <option value="Dermatologist">Dermatologist</option>
                    </select>
                    {providerErrors.specialty && (
                      <span className="error">{providerErrors.specialty}</span>
                    )}
                  </motion.div>
                  <motion.div
                    variants={itemVariants}
                    className="form-group phone"
                  >
                    <label>Phone Number</label>
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="NG"
                      value={providerData.phone}
                      onChange={handleProviderPhoneChange}
                      onBlur={handleProviderPhoneBlur}
                      placeholder="Enter phone number"
                      maxLength={17}
                    />
                    {providerErrors.phone && (
                      <span className="error">{providerErrors.phone}</span>
                    )}
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={providerData.email}
                      onChange={handleProviderChange}
                      onBlur={handleProviderBlur}
                      placeholder="Enter your email address"
                    />
                    {providerErrors.email && (
                      <span className="error">{providerErrors.email}</span>
                    )}
                  </motion.div>
                </motion.form>
              ) : (
                <motion.form
                  onSubmit={handlePatientSubmit}
                  className="edit-profile-form"
                  variants={formVariants}
                >
                  <motion.div
                    variants={itemVariants}
                    className="form-group name-row"
                  >
                    <div className="name-input">
                      <label>First Name</label>
                      <input
                        name="firstName"
                        value={patientData.firstName}
                        onChange={handlePatientChange}
                        onBlur={handlePatientBlur}
                        placeholder="Enter first name"
                      />
                      {patientErrors.firstName && (
                        <span className="error">{patientErrors.firstName}</span>
                      )}
                    </div>
                    <div className="name-input">
                      <label>Last Name</label>
                      <input
                        name="lastName"
                        value={patientData.lastName}
                        onChange={handlePatientChange}
                        onBlur={handlePatientBlur}
                        placeholder="Enter last name"
                      />
                      {patientErrors.lastName && (
                        <span className="error">{patientErrors.lastName}</span>
                      )}
                    </div>
                  </motion.div>
                  <motion.div
                    variants={itemVariants}
                    className="form-group phone"
                  >
                    <label>Phone Number</label>
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="NG"
                      value={patientData.phone}
                      onChange={handlePatientPhoneChange}
                      onBlur={handlePatientPhoneBlur}
                      placeholder="Enter phone number"
                      maxLength={17}
                    />
                    {patientErrors.phone && (
                      <span className="error">{patientErrors.phone}</span>
                    )}
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Gender</label>
                    <select
                      name="gender"
                      value={patientData.gender}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                    >
                      <option value="">Select from dropdown</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {patientErrors.gender && (
                      <span className="error">{patientErrors.gender}</span>
                    )}
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={patientData.email}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter your email address"
                    />
                    {patientErrors.email && (
                      <span className="error">{patientErrors.email}</span>
                    )}
                  </motion.div>
                  <motion.div
                    variants={itemVariants}
                    className="form-group dob"
                  >
                    <label>Date of Birth</label>
                    <div className="dob-inputs">
                      <div className="dob-input">
                        <select
                          name="month"
                          value={patientData.dateOfBirth.month || ""}
                          onChange={(e) =>
                            handlePatientDateChange("month", e.target.value)
                          }
                          onBlur={() => handlePatientDateBlur("month")}
                        >
                          <option value="">Month</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(0, i).toLocaleString("default", {
                                month: "long",
                              })}
                            </option>
                          ))}
                        </select>
                        {patientErrors.month && (
                          <span className="error">{patientErrors.month}</span>
                        )}
                      </div>
                      <div className="dob-input">
                        <input
                          type="text"
                          name="date"
                          value={patientData.dateOfBirth.date}
                          onChange={(e) =>
                            handlePatientDateChange("date", e.target.value)
                          }
                          onBlur={() => handlePatientDateBlur("date")}
                          placeholder="Day"
                          maxLength="2"
                          style={{
                            WebkitAppearance: "none",
                            MozAppearance: "textfield",
                          }}
                        />
                        {patientErrors.date && (
                          <span className="error">{patientErrors.date}</span>
                        )}
                      </div>
                      <div className="dob-input">
                        <input
                          type="text"
                          name="year"
                          value={patientData.dateOfBirth.year}
                          onChange={(e) =>
                            handlePatientDateChange("year", e.target.value)
                          }
                          onBlur={() => handlePatientDateBlur("year")}
                          placeholder="Year"
                          maxLength="4"
                          style={{
                            WebkitAppearance: "none",
                            MozAppearance: "textfield",
                          }}
                        />
                        {patientErrors.year && (
                          <span className="error">{patientErrors.year}</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  <motion.div variants={itemVariants} className="form-group">
                    <label>Address</label>
                    <textarea
                      name="address"
                      value={patientData.address}
                      onChange={handlePatientChange}
                      onBlur={handlePatientBlur}
                      placeholder="Enter your address"
                      rows="4"
                    />
                    {patientErrors.address && (
                      <span className="error">{patientErrors.address}</span>
                    )}
                  </motion.div>
                </motion.form>
              )}
            </div>

            <motion.div className="avatar-section" variants={itemVariants}>
              {avatar ? (
                <img src={avatar} alt="User Avatar" className="avatar-image" />
              ) : (
                <div className="avatar-placeholder">
                  {(isProvider ? providerData.firstName : patientData.firstName)
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
              <div className="avatar-buttons">
                <button
                  className="avatar-button change"
                  onClick={handleChangeAvatar}
                >
                  Change avatar
                </button>
                <button
                  className={`avatar-button remove ${
                    !avatar ? "disabled" : ""
                  }`}
                  onClick={handleRemoveAvatar}
                  disabled={!avatar}
                >
                  Remove avatar
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
            </motion.div>
          </div>

          {apiError && (
            <motion.p className="error-message" variants={itemVariants}>
              {apiError}
            </motion.p>
          )}

          <motion.div variants={itemVariants} className="button-group">
            <motion.button
              type="submit"
              onClick={isProvider ? handleProviderSubmit : handlePatientSubmit}
              className={`save-button ${
                isSubmitting ||
                (isProvider ? isProviderIncomplete() : isPatientIncomplete()) ||
                (isProvider
                  ? Object.values(providerErrors).some((error) => error)
                  : Object.values(patientErrors).some((error) => error))
                  ? "disabled"
                  : ""
              }`}
              disabled={
                isSubmitting ||
                (isProvider ? isProviderIncomplete() : isPatientIncomplete()) ||
                (isProvider
                  ? Object.values(providerErrors).some((error) => error)
                  : Object.values(patientErrors).some((error) => error))
              }
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {isSubmitting ? "Saving..." : "Save changes"}
            </motion.button>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="reset-password-section"
          >
            <span>Can't remember password?</span>
            <button
              onClick={() => navigate("/reset-password")}
              className="reset-password-section-button"
            >
              <Link className="reset-password-link">Reset password</Link>
            </button>
          </motion.div>
        </motion.div>
      </motion.div>

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        withCloseButton={false}
        centered
        size="sm"
        className="success-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <div className="modal-content">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
            alt="Direct by Syndicate Bio"
            className="modal-logo"
          />
          <div className="modal-message-box">
            <Text className="modal-title">Profile updated!</Text>
            <Text className="modal-message">
              Your profile has been successfully updated. You can now manage
              your account with the new information.
            </Text>
            <Button
              onClick={() => setModalOpened(false)}
              className="modal-close-button"
              style={{ height: "fit-content" }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditProfile;
